import { useState, useMemo, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { 
  ArrowLeft, 
  ArrowUpDown, 
  ArrowUp, 
  ArrowDown, 
  Star, 
  TrendingUp, 
  TrendingDown,
  Filter,
  Search,
  Users,
  Trophy,
  Sparkles
} from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

interface Prospect {
  id: number;
  name: string;
  position: string;
  team: string;
  league: string;
  nationality: string;
  height: string;
  weight: string;
  birthDate: string;
  shoots: string;
  rankings: {
    consensus: number;
    eliteProspects: number;
    tsnCraigButton: number;
    bobMckenzie: number;
    thehockeywriters: number;
    fcHockey: number;
  };
  trend: "up" | "down" | "stable";
  tier: "Elite" | "Top" | "First Round" | "Second Round" | "Later";
  scoutingNotes: string;
}

type SortField = "consensus" | "eliteProspects" | "tsnCraigButton" | "bobMckenzie" | "thehockeywriters" | "fcHockey" | "name";
type SortDirection = "asc" | "desc";

const prospects: Prospect[] = [
  {
    id: 1,
    name: "Gavin McKenna",
    position: "LW",
    team: "Penn State Univ.",
    league: "NCAA",
    nationality: "CAN",
    height: "6'0\"",
    weight: "180",
    birthDate: "2007-12-20",
    shoots: "L",
    rankings: { consensus: 1, eliteProspects: 1, tsnCraigButton: 1, bobMckenzie: 1, thehockeywriters: 2, fcHockey: 1 },
    trend: "stable",
    tier: "Elite",
    scoutingNotes: "Generational winger with elite vision, edges, and creativity. Reclaimed No. 1 with dominant freshman run.",
  },
  {
    id: 2,
    name: "Ivar Stenberg",
    position: "LW",
    team: "Frölunda HC",
    league: "SHL",
    nationality: "SWE",
    height: "5'11\"",
    weight: "176",
    birthDate: "2008-02-22",
    shoots: "L",
    rankings: { consensus: 2, eliteProspects: 2, tsnCraigButton: 2, bobMckenzie: 3, thehockeywriters: 1, fcHockey: 2 },
    trend: "stable",
    tier: "Elite",
    scoutingNotes: "Dynamic playmaker tearing up the SHL as a rookie. Pushed McKenna for No. 1 mid-season.",
  },
  {
    id: 3,
    name: "Keaton Verhoeff",
    position: "D",
    team: "Univ. of North Dakota",
    league: "NCAA",
    nationality: "CAN",
    height: "6'3\"",
    weight: "210",
    birthDate: "2008-01-09",
    shoots: "R",
    rankings: { consensus: 3, eliteProspects: 12, tsnCraigButton: 9, bobMckenzie: 2, thehockeywriters: 7, fcHockey: 6 },
    trend: "down",
    tier: "Elite",
    scoutingNotes: "Two-way right-shot dynamo. Already producing in NCAA as a 17-year-old.",
  },
  {
    id: 4,
    name: "Chase Reid",
    position: "D",
    team: "Soo Greyhounds",
    league: "OHL",
    nationality: "USA",
    height: "6'0\"",
    weight: "185",
    birthDate: "2008-01-15",
    shoots: "L",
    rankings: { consensus: 4, eliteProspects: 3, tsnCraigButton: 5, bobMckenzie: 10, thehockeywriters: 8, fcHockey: 3 },
    trend: "stable",
    tier: "Top",
    scoutingNotes: "Smooth offensive defenseman with elite hockey IQ and skating.",
  },
  {
    id: 5,
    name: "Viggo Björck",
    position: "C",
    team: "Djurgårdens IF",
    league: "SHL",
    nationality: "SWE",
    height: "5'10\"",
    weight: "165",
    birthDate: "2008-04-04",
    shoots: "L",
    rankings: { consensus: 5, eliteProspects: 8, tsnCraigButton: 4, bobMckenzie: 8, thehockeywriters: 3, fcHockey: 8 },
    trend: "stable",
    tier: "Top",
    scoutingNotes: "Cerebral playmaker dubbed the 'Swedish Nick Suzuki.'",
  },
  {
    id: 6,
    name: "Carson Carels",
    position: "D",
    team: "Prince George Cougars",
    league: "WHL",
    nationality: "CAN",
    height: "6'3\"",
    weight: "195",
    birthDate: "2008-03-13",
    shoots: "R",
    rankings: { consensus: 6, eliteProspects: 4, tsnCraigButton: 3, bobMckenzie: 11, thehockeywriters: 23, fcHockey: 5 },
    trend: "up",
    tier: "Top",
    scoutingNotes: "Mobile two-way defender with massive offensive breakout this season.",
  },
  {
    id: 7,
    name: "Tynan Lawrence",
    position: "C",
    team: "Boston Univ.",
    league: "NCAA",
    nationality: "CAN",
    height: "6'1\"",
    weight: "180",
    birthDate: "2008-02-05",
    shoots: "L",
    rankings: { consensus: 7, eliteProspects: 16, tsnCraigButton: 21, bobMckenzie: 6, thehockeywriters: 4, fcHockey: 12 },
    trend: "down",
    tier: "Top",
    scoutingNotes: "Skilled center making the jump to NCAA. Skating and pace stand out.",
  },
  {
    id: 8,
    name: "Ethan Belchetz",
    position: "LW",
    team: "Windsor Spitfires",
    league: "OHL",
    nationality: "CAN",
    height: "6'5\"",
    weight: "225",
    birthDate: "2008-02-10",
    shoots: "L",
    rankings: { consensus: 8, eliteProspects: 9, tsnCraigButton: 12, bobMckenzie: 5, thehockeywriters: 14, fcHockey: 13 },
    trend: "down",
    tier: "Top",
    scoutingNotes: "Power forward with a heavy shot and developing playmaking.",
  },
  {
    id: 9,
    name: "Daxon Rudolph",
    position: "D",
    team: "Prince Albert Raiders",
    league: "WHL",
    nationality: "CAN",
    height: "6'2\"",
    weight: "195",
    birthDate: "2008-02-19",
    shoots: "R",
    rankings: { consensus: 9, eliteProspects: 7, tsnCraigButton: 7, bobMckenzie: 9, thehockeywriters: 9, fcHockey: 10 },
    trend: "stable",
    tier: "Top",
    scoutingNotes: "Two-way blueliner with leadership and offensive instincts.",
  },
  {
    id: 10,
    name: "Ryan Lin",
    position: "D",
    team: "Vancouver Giants",
    league: "WHL",
    nationality: "CAN",
    height: "5'10\"",
    weight: "170",
    birthDate: "2008-02-13",
    shoots: "R",
    rankings: { consensus: 10, eliteProspects: 15, tsnCraigButton: 17, bobMckenzie: 7, thehockeywriters: 16, fcHockey: 18 },
    trend: "down",
    tier: "Top",
    scoutingNotes: "Undersized but elite-skating puck-mover. Drives play from the back end.",
  },
  {
    id: 11,
    name: "Caleb Malhotra",
    position: "C",
    team: "Brantford Bulldogs",
    league: "OHL",
    nationality: "CAN",
    height: "6'0\"",
    weight: "185",
    birthDate: "2008-01-29",
    shoots: "L",
    rankings: { consensus: 11, eliteProspects: 5, tsnCraigButton: 6, bobMckenzie: 11, thehockeywriters: 15, fcHockey: 4 },
    trend: "up",
    tier: "First Round",
    scoutingNotes: "Son of Manny; complete two-way pivot with strong production.",
  },
  {
    id: 12,
    name: "Adam Novotny",
    position: "LW",
    team: "Peterborough Petes",
    league: "OHL",
    nationality: "CZE",
    height: "6'2\"",
    weight: "195",
    birthDate: "2008-03-08",
    shoots: "L",
    rankings: { consensus: 12, eliteProspects: 14, tsnCraigButton: 23, bobMckenzie: 12, thehockeywriters: 13, fcHockey: 14 },
    trend: "down",
    tier: "First Round",
    scoutingNotes: "Czech import with a goal-scoring touch in the OHL.",
  },
  {
    id: 13,
    name: "Alberts Smits",
    position: "D",
    team: "Jukurit",
    league: "Liiga",
    nationality: "LAT",
    height: "6'2\"",
    weight: "190",
    birthDate: "2008-01-15",
    shoots: "L",
    rankings: { consensus: 13, eliteProspects: 11, tsnCraigButton: 8, bobMckenzie: 13, thehockeywriters: 5, fcHockey: 7 },
    trend: "up",
    tier: "First Round",
    scoutingNotes: "Latvian defender already producing in Liiga as a teenager.",
  },
  {
    id: 14,
    name: "Malte Gustafsson",
    position: "D",
    team: "HV71 U20",
    league: "SuperElit",
    nationality: "SWE",
    height: "6'2\"",
    weight: "185",
    birthDate: "2008-04-01",
    shoots: "L",
    rankings: { consensus: 14, eliteProspects: 10, tsnCraigButton: 11, bobMckenzie: 16, thehockeywriters: 6, fcHockey: 9 },
    trend: "up",
    tier: "First Round",
    scoutingNotes: "Smooth two-way defender impressing on the Swedish U18 stage.",
  },
  {
    id: 15,
    name: "Oscar Hemming",
    position: "F",
    team: "Boston College",
    league: "NCAA",
    nationality: "FIN",
    height: "6'1\"",
    weight: "185",
    birthDate: "2008-01-20",
    shoots: "L",
    rankings: { consensus: 15, eliteProspects: 18, tsnCraigButton: 15, bobMckenzie: 15, thehockeywriters: 17, fcHockey: 11 },
    trend: "stable",
    tier: "First Round",
    scoutingNotes: "Finnish forward making waves at Boston College.",
  },
  {
    id: 16,
    name: "Xavier Villeneuve",
    position: "D",
    team: "Blainville-Boisbriand Armada",
    league: "QMJHL",
    nationality: "CAN",
    height: "5'10\"",
    weight: "170",
    birthDate: "2008-03-25",
    shoots: "L",
    rankings: { consensus: 16, eliteProspects: 13, tsnCraigButton: 16, bobMckenzie: 16, thehockeywriters: 10, fcHockey: 20 },
    trend: "stable",
    tier: "First Round",
    scoutingNotes: "Elite-skating offensive D with dynamic puck skill.",
  },
  {
    id: 17,
    name: "Oliver Suvanto",
    position: "C",
    team: "Tappara",
    league: "Liiga",
    nationality: "FIN",
    height: "6'2\"",
    weight: "190",
    birthDate: "2008-02-15",
    shoots: "L",
    rankings: { consensus: 17, eliteProspects: 28, tsnCraigButton: 14, bobMckenzie: 17, thehockeywriters: 18, fcHockey: 17 },
    trend: "down",
    tier: "First Round",
    scoutingNotes: "Big two-way center cutting his teeth in Liiga.",
  },
  {
    id: 18,
    name: "Elton Hermansson",
    position: "RW",
    team: "MoDo Hockey",
    league: "Allsvenskan",
    nationality: "SWE",
    height: "6'0\"",
    weight: "180",
    birthDate: "2008-03-05",
    shoots: "R",
    rankings: { consensus: 18, eliteProspects: 25, tsnCraigButton: 18, bobMckenzie: 14, thehockeywriters: 12, fcHockey: 15 },
    trend: "down",
    tier: "First Round",
    scoutingNotes: "Sniper from MoDo with above-average tools across the board.",
  },
  {
    id: 19,
    name: "J.P. Hurlbert",
    position: "F",
    team: "Kamloops Blazers",
    league: "WHL",
    nationality: "USA",
    height: "6'1\"",
    weight: "185",
    birthDate: "2008-01-30",
    shoots: "L",
    rankings: { consensus: 19, eliteProspects: 30, tsnCraigButton: 22, bobMckenzie: 19, thehockeywriters: 19, fcHockey: 26 },
    trend: "down",
    tier: "First Round",
    scoutingNotes: "WHL goal-scorer lighting up the league.",
  },
  {
    id: 20,
    name: "Mathis Preston",
    position: "F",
    team: "Spokane Chiefs",
    league: "WHL",
    nationality: "CAN",
    height: "6'0\"",
    weight: "180",
    birthDate: "2008-02-25",
    shoots: "L",
    rankings: { consensus: 20, eliteProspects: 20, tsnCraigButton: 20, bobMckenzie: 13, thehockeywriters: 9, fcHockey: 24 },
    trend: "stable",
    tier: "First Round",
    scoutingNotes: "Skilled WHL forward with high-end pace.",
  },
  {
    id: 21,
    name: "Juho Piiparinen",
    position: "D",
    team: "Tappara",
    league: "Liiga",
    nationality: "FIN",
    height: "6'4\"",
    weight: "200",
    birthDate: "2008-03-18",
    shoots: "L",
    rankings: { consensus: 21, eliteProspects: 21, tsnCraigButton: 19, bobMckenzie: 21, thehockeywriters: 25, fcHockey: 27 },
    trend: "stable",
    tier: "First Round",
    scoutingNotes: "Big Finnish defender developing in Liiga.",
  },
  {
    id: 22,
    name: "Marcus Nordmark",
    position: "LW",
    team: "Djurgårdens IF U20",
    league: "SuperElit",
    nationality: "SWE",
    height: "5'11\"",
    weight: "180",
    birthDate: "2008-02-10",
    shoots: "L",
    rankings: { consensus: 22, eliteProspects: 22, tsnCraigButton: 22, bobMckenzie: 15, thehockeywriters: 29, fcHockey: 22 },
    trend: "stable",
    tier: "First Round",
    scoutingNotes: "Skilled Swedish winger dominating at the U20 level.",
  },
  {
    id: 23,
    name: "Ilia Morozov",
    position: "F",
    team: "Miami Univ. (Ohio)",
    league: "NCAA",
    nationality: "RUS",
    height: "5'11\"",
    weight: "175",
    birthDate: "2008-01-05",
    shoots: "L",
    rankings: { consensus: 23, eliteProspects: 21, tsnCraigButton: 25, bobMckenzie: 23, thehockeywriters: 28, fcHockey: 22 },
    trend: "stable",
    tier: "First Round",
    scoutingNotes: "Crafty Russian forward in the NCAA.",
  },
  {
    id: 24,
    name: "Yegor Shilov",
    position: "C",
    team: "Victoriaville Tigres",
    league: "QMJHL",
    nationality: "RUS",
    height: "6'0\"",
    weight: "185",
    birthDate: "2008-02-28",
    shoots: "L",
    rankings: { consensus: 24, eliteProspects: 26, tsnCraigButton: 18, bobMckenzie: 24, thehockeywriters: 24, fcHockey: 24 },
    trend: "stable",
    tier: "First Round",
    scoutingNotes: "Russian import producing in the QMJHL.",
  },
  {
    id: 25,
    name: "Nikita Klepov",
    position: "F",
    team: "Saginaw Spirit",
    league: "OHL",
    nationality: "USA",
    height: "6'1\"",
    weight: "185",
    birthDate: "2008-03-01",
    shoots: "L",
    rankings: { consensus: 25, eliteProspects: 20, tsnCraigButton: 20, bobMckenzie: 25, thehockeywriters: 25, fcHockey: 21 },
    trend: "up",
    tier: "First Round",
    scoutingNotes: "High-scoring OHL winger with U.S./Russian heritage.",
  },
  {
    id: 26,
    name: "Alexander Command",
    position: "C",
    team: "Örebro HK U20",
    league: "SuperElit",
    nationality: "SWE",
    height: "6'1\"",
    weight: "185",
    birthDate: "2008-04-10",
    shoots: "L",
    rankings: { consensus: 26, eliteProspects: 19, tsnCraigButton: 13, bobMckenzie: 26, thehockeywriters: 26, fcHockey: 23 },
    trend: "up",
    tier: "First Round",
    scoutingNotes: "Strong two-way Swedish center.",
  },
  {
    id: 27,
    name: "Ryan Roobroeck",
    position: "C",
    team: "Niagara IceDogs",
    league: "OHL",
    nationality: "CAN",
    height: "6'4\"",
    weight: "210",
    birthDate: "2008-01-21",
    shoots: "L",
    rankings: { consensus: 27, eliteProspects: 27, tsnCraigButton: 27, bobMckenzie: 4, thehockeywriters: 27, fcHockey: 27 },
    trend: "stable",
    tier: "First Round",
    scoutingNotes: "Big-bodied playmaker, high-end IQ.",
  },
  {
    id: 28,
    name: "Brooks Rogowski",
    position: "C",
    team: "Oshawa Generals",
    league: "OHL",
    nationality: "USA",
    height: "6'0\"",
    weight: "180",
    birthDate: "2008-02-14",
    shoots: "L",
    rankings: { consensus: 28, eliteProspects: 29, tsnCraigButton: 28, bobMckenzie: 28, thehockeywriters: 28, fcHockey: 28 },
    trend: "stable",
    tier: "First Round",
    scoutingNotes: "Skilled American center producing in the OHL.",
  },
  {
    id: 29,
    name: "Wyatt Cullen",
    position: "F",
    team: "U.S. National U18 Team",
    league: "USNTDP",
    nationality: "USA",
    height: "6'1\"",
    weight: "185",
    birthDate: "2008-02-08",
    shoots: "L",
    rankings: { consensus: 29, eliteProspects: 6, tsnCraigButton: 24, bobMckenzie: 29, thehockeywriters: 11, fcHockey: 19 },
    trend: "up",
    tier: "First Round",
    scoutingNotes: "Smart all-situations forward on the U.S. NTDP.",
  },
  {
    id: 30,
    name: "William Håkansson",
    position: "D",
    team: "Luleå HF",
    league: "SHL",
    nationality: "SWE",
    height: "6'1\"",
    weight: "190",
    birthDate: "2008-03-22",
    shoots: "L",
    rankings: { consensus: 30, eliteProspects: 30, tsnCraigButton: 30, bobMckenzie: 30, thehockeywriters: 19, fcHockey: 30 },
    trend: "stable",
    tier: "First Round",
    scoutingNotes: "Defensive-minded SHL rookie.",
  },
  {
    id: 31,
    name: "Jack Hextall",
    position: "C",
    team: "Youngstown Phantoms",
    league: "USHL",
    nationality: "USA",
    height: "6'0\"",
    weight: "180",
    birthDate: "2008-02-04",
    shoots: "R",
    rankings: { consensus: 31, eliteProspects: 31, tsnCraigButton: 26, bobMckenzie: 31, thehockeywriters: 20, fcHockey: 31 },
    trend: "up",
    tier: "First Round",
    scoutingNotes: "From the Hextall hockey lineage; strong two-way center.",
  },
  {
    id: 32,
    name: "Maddox Dagenais",
    position: "C",
    team: "Québec Remparts",
    league: "QMJHL",
    nationality: "CAN",
    height: "6'0\"",
    weight: "185",
    birthDate: "2008-02-22",
    shoots: "L",
    rankings: { consensus: 32, eliteProspects: 17, tsnCraigButton: 32, bobMckenzie: 32, thehockeywriters: 32, fcHockey: 16 },
    trend: "up",
    tier: "First Round",
    scoutingNotes: "Goal-scoring center in the QMJHL.",
  },
  {
    id: 33,
    name: "Nikita Shcherbakov",
    position: "D",
    team: "Salavat Yulaev Ufa",
    league: "KHL",
    nationality: "RUS",
    height: "6'3\"",
    weight: "200",
    birthDate: "2008-01-12",
    shoots: "L",
    rankings: { consensus: 33, eliteProspects: 27, tsnCraigButton: 33, bobMckenzie: 33, thehockeywriters: 33, fcHockey: 33 },
    trend: "up",
    tier: "Second Round",
    scoutingNotes: "Russian defender already in the KHL.",
  },
  {
    id: 34,
    name: "Liam Ruck",
    position: "F",
    team: "Medicine Hat Tigers",
    league: "WHL",
    nationality: "CAN",
    height: "6'0\"",
    weight: "180",
    birthDate: "2008-03-15",
    shoots: "L",
    rankings: { consensus: 34, eliteProspects: 24, tsnCraigButton: 10, bobMckenzie: 34, thehockeywriters: 34, fcHockey: 34 },
    trend: "up",
    tier: "Second Round",
    scoutingNotes: "One of the Ruck twins; goal-scoring star in Medicine Hat's championship push.",
  },
  {
    id: 35,
    name: "Niklas Aaram-Olsen",
    position: "RW",
    team: "Örebro HK U20",
    league: "SuperElit",
    nationality: "NOR",
    height: "6'0\"",
    weight: "185",
    birthDate: "2008-02-19",
    shoots: "L",
    rankings: { consensus: 35, eliteProspects: 35, tsnCraigButton: 35, bobMckenzie: 35, thehockeywriters: 35, fcHockey: 35 },
    trend: "stable",
    tier: "Second Round",
    scoutingNotes: "Norwegian winger producing in Swedish junior.",
  },
  {
    id: 36,
    name: "Casey Mutryn",
    position: "F",
    team: "U.S. National U18 Team",
    league: "USNTDP",
    nationality: "USA",
    height: "6'0\"",
    weight: "185",
    birthDate: "2008-03-04",
    shoots: "L",
    rankings: { consensus: 36, eliteProspects: 31, tsnCraigButton: 36, bobMckenzie: 36, thehockeywriters: 21, fcHockey: 36 },
    trend: "up",
    tier: "Second Round",
    scoutingNotes: "Physical, hard-working U.S. NTDP forward.",
  },
  {
    id: 37,
    name: "Adam Valentini",
    position: "F",
    team: "Univ. of Michigan",
    league: "NCAA",
    nationality: "CAN",
    height: "6'0\"",
    weight: "185",
    birthDate: "2008-01-25",
    shoots: "L",
    rankings: { consensus: 37, eliteProspects: 37, tsnCraigButton: 37, bobMckenzie: 37, thehockeywriters: 26, fcHockey: 37 },
    trend: "stable",
    tier: "Second Round",
    scoutingNotes: "Canadian forward at Michigan with two-way upside.",
  },
  {
    id: 38,
    name: "Tommy Bleyl",
    position: "D",
    team: "Moncton Wildcats",
    league: "QMJHL",
    nationality: "USA",
    height: "6'2\"",
    weight: "190",
    birthDate: "2008-02-17",
    shoots: "L",
    rankings: { consensus: 38, eliteProspects: 32, tsnCraigButton: 27, bobMckenzie: 38, thehockeywriters: 27, fcHockey: 25 },
    trend: "up",
    tier: "Second Round",
    scoutingNotes: "Offensive defenseman dominating QMJHL scoring from the back end.",
  },
  {
    id: 39,
    name: "Simas Ignatavicius",
    position: "C",
    team: "Genève-Servette HC",
    league: "NL",
    nationality: "LTU",
    height: "6'2\"",
    weight: "195",
    birthDate: "2008-02-01",
    shoots: "L",
    rankings: { consensus: 39, eliteProspects: 39, tsnCraigButton: 39, bobMckenzie: 39, thehockeywriters: 31, fcHockey: 39 },
    trend: "stable",
    tier: "Second Round",
    scoutingNotes: "Lithuanian forward in the Swiss NL.",
  },
  {
    id: 40,
    name: "Jaxon Cover",
    position: "LW",
    team: "London Knights",
    league: "OHL",
    nationality: "CAN",
    height: "6'0\"",
    weight: "185",
    birthDate: "2008-03-09",
    shoots: "L",
    rankings: { consensus: 40, eliteProspects: 23, tsnCraigButton: 28, bobMckenzie: 40, thehockeywriters: 30, fcHockey: 29 },
    trend: "up",
    tier: "Second Round",
    scoutingNotes: "Knights winger with two-way reliability.",
  },
  {
    id: 41,
    name: "Tomas Chrenko",
    position: "C",
    team: "HK Nitra",
    league: "Slovakia",
    nationality: "SVK",
    height: "6'0\"",
    weight: "180",
    birthDate: "2008-02-26",
    shoots: "L",
    rankings: { consensus: 41, eliteProspects: 41, tsnCraigButton: 31, bobMckenzie: 41, thehockeywriters: 41, fcHockey: 41 },
    trend: "up",
    tier: "Second Round",
    scoutingNotes: "Slovak center playing pro at 17.",
  },
  {
    id: 42,
    name: "Tobias Trejbal",
    position: "G",
    team: "Youngstown Phantoms",
    league: "USHL",
    nationality: "CZE",
    height: "6'3\"",
    weight: "185",
    birthDate: "2008-01-14",
    shoots: "L",
    rankings: { consensus: 42, eliteProspects: 42, tsnCraigButton: 42, bobMckenzie: 42, thehockeywriters: 42, fcHockey: 42 },
    trend: "stable",
    tier: "Second Round",
    scoutingNotes: "Top European goaltending prospect.",
  },
  {
    id: 43,
    name: "Adam Goljer",
    position: "D",
    team: "HK Dukla Trencin",
    league: "Slovakia",
    nationality: "SVK",
    height: "6'2\"",
    weight: "190",
    birthDate: "2008-02-12",
    shoots: "L",
    rankings: { consensus: 43, eliteProspects: 43, tsnCraigButton: 43, bobMckenzie: 43, thehockeywriters: 24, fcHockey: 28 },
    trend: "stable",
    tier: "Second Round",
    scoutingNotes: "Slovak two-way defender.",
  },
  {
    id: 44,
    name: "Beckham Edwards",
    position: "C",
    team: "Sarnia Sting",
    league: "OHL",
    nationality: "CAN",
    height: "5'11\"",
    weight: "175",
    birthDate: "2008-03-18",
    shoots: "L",
    rankings: { consensus: 44, eliteProspects: 44, tsnCraigButton: 44, bobMckenzie: 44, thehockeywriters: 44, fcHockey: 44 },
    trend: "stable",
    tier: "Second Round",
    scoutingNotes: "Skilled OHL forward.",
  },
  {
    id: 45,
    name: "Markus Ruck",
    position: "F",
    team: "Medicine Hat Tigers",
    league: "WHL",
    nationality: "CAN",
    height: "6'0\"",
    weight: "180",
    birthDate: "2008-03-15",
    shoots: "R",
    rankings: { consensus: 45, eliteProspects: 45, tsnCraigButton: 16, bobMckenzie: 45, thehockeywriters: 45, fcHockey: 45 },
    trend: "up",
    tier: "Second Round",
    scoutingNotes: "The other Ruck twin; elite playmaker leading WHL scoring.",
  },
];

const scoutSources = [
  { id: "consensus", name: "EP Consolidated", description: "Consolidated ranking (EliteProspects)" },
  { id: "eliteProspects", name: "Elite Prospects", description: "EP Top 32 — Apr 2026" },
  { id: "tsnCraigButton", name: "TSN Craig Button", description: "Craig's List — May 2026" },
  { id: "bobMckenzie", name: "TSN Chris Peters", description: "TSN/Chris Peters Top 32" },
  { id: "thehockeywriters", name: "THN / Ferrari", description: "The Hockey News / Ferrari Top 32" },
  { id: "fcHockey", name: "McKeen's Hockey", description: "McKeen's Hockey Top 32" },
];

const getFlagEmoji = (country: string): string => {
  const flags: Record<string, string> = {
    CAN: "🇨🇦",
    USA: "🇺🇸",
    SWE: "🇸🇪",
    FIN: "🇫🇮",
    RUS: "🇷🇺",
    CZE: "🇨🇿",
    SVK: "🇸🇰",
    GER: "🇩🇪",
    CHE: "🇨🇭",
    BLR: "🇧🇾",
    AUT: "🇦🇹",
    DEN: "🇩🇰",
    NOR: "🇳🇴",
    LAT: "🇱🇻",
    LTU: "🇱🇹",
  };
  return flags[country] || "🏒";
};

const getTierColor = (tier: string): string => {
  switch (tier) {
    case "Elite":
      return "bg-primary/20 text-primary border-primary/30";
    case "Top":
      return "bg-amber-500/20 text-amber-500 border-amber-500/30";
    case "First Round":
      return "bg-emerald-500/20 text-emerald-500 border-emerald-500/30";
    case "Second Round":
      return "bg-blue-500/20 text-blue-500 border-blue-500/30";
    default:
      return "bg-muted text-muted-foreground border-border";
  }
};

const DraftRankings = () => {
  const navigate = useNavigate();
  const [sortField, setSortField] = useState<SortField>("consensus");
  const [sortDirection, setSortDirection] = useState<SortDirection>("asc");
  const [searchQuery, setSearchQuery] = useState("");
  const [positionFilter, setPositionFilter] = useState<string>("all");
  const [leagueFilter, setLeagueFilter] = useState<string>("all");
  const [tierFilter, setTierFilter] = useState<string>("all");
  const [selectedProspect, setSelectedProspect] = useState<Prospect | null>(null);

  // Scroll to top when page loads
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDirection("asc");
    }
  };

  const sortedProspects = useMemo(() => {
    let filtered = [...prospects];

    // Apply search filter
    if (searchQuery) {
      filtered = filtered.filter(p =>
        p.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        p.team.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    // Apply position filter
    if (positionFilter !== "all") {
      filtered = filtered.filter(p => p.position === positionFilter);
    }

    // Apply league filter
    if (leagueFilter !== "all") {
      filtered = filtered.filter(p => p.league === leagueFilter);
    }

    // Apply tier filter
    if (tierFilter !== "all") {
      filtered = filtered.filter(p => p.tier === tierFilter);
    }

    // Sort
    filtered.sort((a, b) => {
      let aValue: number | string;
      let bValue: number | string;

      if (sortField === "name") {
        aValue = a.name;
        bValue = b.name;
      } else {
        aValue = a.rankings[sortField];
        bValue = b.rankings[sortField];
      }

      if (typeof aValue === "string" && typeof bValue === "string") {
        return sortDirection === "asc"
          ? aValue.localeCompare(bValue)
          : bValue.localeCompare(aValue);
      }

      return sortDirection === "asc"
        ? (aValue as number) - (bValue as number)
        : (bValue as number) - (aValue as number);
    });

    return filtered;
  }, [searchQuery, positionFilter, leagueFilter, tierFilter, sortField, sortDirection]);

  const SortHeader = ({ field, label }: { field: SortField; label: string }) => (
    <TableHead
      className="text-muted-foreground cursor-pointer hover:text-foreground transition-colors text-center"
      onClick={() => handleSort(field)}
    >
      <div className="flex items-center justify-center gap-1">
        <span>{label}</span>
        {sortField === field ? (
          sortDirection === "asc" ? (
            <ArrowUp className="w-3 h-3" />
          ) : (
            <ArrowDown className="w-3 h-3" />
          )
        ) : (
          <ArrowUpDown className="w-3 h-3 opacity-50" />
        )}
      </div>
    </TableHead>
  );

  const uniqueLeagues = [...new Set(prospects.map(p => p.league))];
  const uniquePositions = [...new Set(prospects.map(p => p.position))];

  return (
    <div className="min-h-screen bg-background pt-16">
      <Navbar />
      <main className="container mx-auto px-6 py-8">
        {/* Header */}
        <div className="mb-8">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate("/")}
            className="mb-4 gap-2 text-muted-foreground hover:text-foreground"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Home
          </Button>
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <h1 className="font-display text-4xl font-bold text-foreground flex items-center gap-3">
                <Star className="w-8 h-8 text-primary" />
                2026 NHL Draft Rankings
              </h1>
              <p className="text-muted-foreground mt-2">
                Compare prospect rankings from top scouts and analysts
              </p>
            </div>
            <div className="flex items-center gap-2">
              <Badge variant="outline" className="px-3 py-1">
                <Users className="w-4 h-4 mr-1" />
                {prospects.length} Prospects
              </Badge>
              <Badge variant="outline" className="px-3 py-1 bg-primary/10 text-primary border-primary/30">
                <Sparkles className="w-4 h-4 mr-1" />
                Updated May 2026
              </Badge>
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="glass-card p-4 mb-6">
          <div className="flex flex-col lg:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Search prospects or teams..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9"
              />
            </div>
            <div className="flex flex-wrap gap-3">
              <Select value={positionFilter} onValueChange={setPositionFilter}>
                <SelectTrigger className="w-32">
                  <SelectValue placeholder="Position" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Positions</SelectItem>
                  {uniquePositions.map(pos => (
                    <SelectItem key={pos} value={pos}>{pos}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={leagueFilter} onValueChange={setLeagueFilter}>
                <SelectTrigger className="w-36">
                  <SelectValue placeholder="League" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Leagues</SelectItem>
                  {uniqueLeagues.map(league => (
                    <SelectItem key={league} value={league}>{league}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={tierFilter} onValueChange={setTierFilter}>
                <SelectTrigger className="w-36">
                  <SelectValue placeholder="Tier" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Tiers</SelectItem>
                  <SelectItem value="Elite">Elite</SelectItem>
                  <SelectItem value="Top">Top</SelectItem>
                  <SelectItem value="First Round">First Round</SelectItem>
                  <SelectItem value="Second Round">Second Round</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>

        {/* Scout Sources Legend */}
        <div className="glass-card p-4 mb-6">
          <h3 className="text-sm font-semibold text-foreground mb-3 flex items-center gap-2">
            <Trophy className="w-4 h-4 text-primary" />
            Scout Sources
          </h3>
          <div className="flex flex-wrap gap-4">
            {scoutSources.map(source => (
              <div key={source.id} className="flex items-center gap-2">
                <div className="w-2 h-2 rounded-full bg-primary" />
                <span className="text-sm font-medium text-foreground">{source.name}</span>
                <span className="text-xs text-muted-foreground">({source.description})</span>
              </div>
            ))}
          </div>
        </div>

        {/* Main Rankings Table */}
        <Tabs defaultValue="rankings" className="w-full">
          <TabsList className="mb-6">
            <TabsTrigger value="rankings" className="flex items-center gap-2">
              <ArrowUpDown className="w-4 h-4" />
              Rankings Board
            </TabsTrigger>
            <TabsTrigger value="comparison" className="flex items-center gap-2">
              <Filter className="w-4 h-4" />
              Scout Comparison
            </TabsTrigger>
          </TabsList>

          <TabsContent value="rankings">
            <div className="glass-card overflow-hidden">
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="border-border/50 hover:bg-transparent">
                      <SortHeader field="consensus" label="#" />
                      <TableHead className="text-muted-foreground">Prospect</TableHead>
                      <TableHead className="text-muted-foreground text-center">Pos</TableHead>
                      <TableHead className="text-muted-foreground">Team</TableHead>
                      <TableHead className="text-muted-foreground text-center">Tier</TableHead>
                      <TableHead className="text-muted-foreground text-center">Trend</TableHead>
                      <SortHeader field="eliteProspects" label="EP" />
                      <SortHeader field="tsnCraigButton" label="Button" />
                      <SortHeader field="bobMckenzie" label="Peters" />
                      <SortHeader field="thehockeywriters" label="THN" />
                      <SortHeader field="fcHockey" label="McKeen" />
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {sortedProspects.map((prospect) => (
                      <TableRow
                        key={prospect.id}
                        className="border-border/30 hover:bg-muted/30 cursor-pointer"
                        onClick={() => setSelectedProspect(selectedProspect?.id === prospect.id ? null : prospect)}
                      >
                        <TableCell className="font-mono font-bold text-primary text-center">
                          {prospect.rankings.consensus}
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <span className="text-lg">{getFlagEmoji(prospect.nationality)}</span>
                            <div>
                              <span className="font-medium text-foreground">{prospect.name}</span>
                              <p className="text-xs text-muted-foreground">{prospect.height} • {prospect.weight} lbs</p>
                            </div>
                          </div>
                        </TableCell>
                        <TableCell className="text-center">
                          <Badge variant="outline" className="font-mono">{prospect.position}</Badge>
                        </TableCell>
                        <TableCell>
                          <div className="flex flex-col">
                            <span className="text-sm font-medium text-foreground">{prospect.team}</span>
                            <span className="text-xs text-muted-foreground">{prospect.league}</span>
                          </div>
                        </TableCell>
                        <TableCell className="text-center">
                          <Badge variant="outline" className={getTierColor(prospect.tier)}>
                            {prospect.tier}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-center">
                          {prospect.trend === "up" && (
                            <TrendingUp className="w-4 h-4 text-score-win mx-auto" />
                          )}
                          {prospect.trend === "down" && (
                            <TrendingDown className="w-4 h-4 text-score-loss mx-auto" />
                          )}
                          {prospect.trend === "stable" && (
                            <div className="w-4 h-0.5 bg-muted-foreground mx-auto" />
                          )}
                        </TableCell>
                        <TableCell className="text-center font-mono">{prospect.rankings.eliteProspects}</TableCell>
                        <TableCell className="text-center font-mono">{prospect.rankings.tsnCraigButton}</TableCell>
                        <TableCell className="text-center font-mono">{prospect.rankings.bobMckenzie}</TableCell>
                        <TableCell className="text-center font-mono">{prospect.rankings.thehockeywriters}</TableCell>
                        <TableCell className="text-center font-mono">{prospect.rankings.fcHockey}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </div>

            {/* Selected Prospect Detail */}
            {selectedProspect && (
              <div className="mt-6 glass-card p-6">
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-4">
                    <div className="w-16 h-16 rounded-xl bg-primary/20 flex items-center justify-center text-3xl">
                      {getFlagEmoji(selectedProspect.nationality)}
                    </div>
                    <div>
                      <h3 className="font-display text-2xl font-bold text-foreground">{selectedProspect.name}</h3>
                      <p className="text-muted-foreground">{selectedProspect.team} • {selectedProspect.league}</p>
                    </div>
                  </div>
                  <Badge variant="outline" className={`text-lg px-4 py-2 ${getTierColor(selectedProspect.tier)}`}>
                    #{selectedProspect.rankings.consensus} Overall
                  </Badge>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-6 gap-4 mt-6">
                  <div className="bg-muted/30 rounded-xl p-3 text-center">
                    <p className="text-xs text-muted-foreground">Position</p>
                    <p className="font-bold text-foreground">{selectedProspect.position}</p>
                  </div>
                  <div className="bg-muted/30 rounded-xl p-3 text-center">
                    <p className="text-xs text-muted-foreground">Height</p>
                    <p className="font-bold text-foreground">{selectedProspect.height}</p>
                  </div>
                  <div className="bg-muted/30 rounded-xl p-3 text-center">
                    <p className="text-xs text-muted-foreground">Weight</p>
                    <p className="font-bold text-foreground">{selectedProspect.weight} lbs</p>
                  </div>
                  <div className="bg-muted/30 rounded-xl p-3 text-center">
                    <p className="text-xs text-muted-foreground">Shoots</p>
                    <p className="font-bold text-foreground">{selectedProspect.shoots}</p>
                  </div>
                  <div className="bg-muted/30 rounded-xl p-3 text-center">
                    <p className="text-xs text-muted-foreground">Birth Date</p>
                    <p className="font-bold text-foreground">{selectedProspect.birthDate}</p>
                  </div>
                  <div className="bg-muted/30 rounded-xl p-3 text-center">
                    <p className="text-xs text-muted-foreground">Nationality</p>
                    <p className="font-bold text-foreground">{selectedProspect.nationality}</p>
                  </div>
                </div>
                <div className="mt-6 p-4 bg-muted/20 rounded-xl border border-border/50">
                  <h4 className="font-semibold text-foreground mb-2">Scouting Report</h4>
                  <p className="text-muted-foreground">{selectedProspect.scoutingNotes}</p>
                </div>
              </div>
            )}
          </TabsContent>

          <TabsContent value="comparison">
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {scoutSources.map(source => (
                <div key={source.id} className="glass-card p-4">
                  <h4 className="font-display font-bold text-foreground mb-1">{source.name}</h4>
                  <p className="text-xs text-muted-foreground mb-4">{source.description}</p>
                  <div className="space-y-2">
                    {[...prospects]
                      .sort((a, b) => a.rankings[source.id as keyof typeof a.rankings] - b.rankings[source.id as keyof typeof b.rankings])
                      .slice(0, 10)
                      .map((prospect, index) => (
                        <div
                          key={prospect.id}
                          className="flex items-center gap-3 p-2 rounded-lg hover:bg-muted/50 transition-colors"
                        >
                          <span className="font-mono font-bold text-primary w-6">{index + 1}</span>
                          <span className="text-lg">{getFlagEmoji(prospect.nationality)}</span>
                          <span className="flex-1 font-medium text-foreground text-sm">{prospect.name}</span>
                          <Badge variant="outline" className="text-xs">{prospect.position}</Badge>
                        </div>
                      ))}
                  </div>
                </div>
              ))}
            </div>
          </TabsContent>
        </Tabs>
      </main>
      <Footer />
    </div>
  );
};

export default DraftRankings;
